// Themes, fonts and grid density.
//
// The palettes are taken from the 2026 stage art rather than invented: each poster
// runs its own scheme over the same jungle, so they make a natural theme set and
// somebody who camps at one stage all weekend can have the app look like it.
//
// A theme is a set of custom properties written onto the root element. Nothing
// reads a color from JavaScript, so a theme change is one attribute and one style
// write rather than a re-render.

export interface Theme {
  id: string;
  label: string;
  note: string;
  vars: Record<string, string>;
}

export const THEMES: Theme[] = [
  {
    id: 'poster',
    label: 'Lost Lands',
    note: 'The Sunday poster: hot magenta and cyan over deep violet',
    vars: {
      '--bg': '#160a2e',
      '--bg-raised': '#241150',
      '--bg-sunken': '#0e0620',
      '--line': '#41207d',
      '--line-strong': '#6b31c4',
      '--ink': '#fdf4ff',
      '--ink-dim': '#c9a6ef',
      '--ink-faint': '#9b7bc4',
      '--accent': '#ff2fd0',
      '--accent-cool': '#2ff0ff',
      '--gold': '#ffe14d',
      '--good': '#4dff9e',
      '--warn': '#ff5e5e',
      '--glow': '0 0 22px rgba(255, 47, 208, 0.45)',
    },
  },
  {
    id: 'saturday',
    label: 'Saturday',
    note: 'Jungle laser green over black',
    vars: {
      '--bg': '#04140a',
      '--bg-raised': '#0a2915',
      '--bg-sunken': '#020c06',
      '--line': '#14512a',
      '--line-strong': '#1f8442',
      '--ink': '#f0fff4',
      '--ink-dim': '#93e5ad',
      '--ink-faint': '#5fae7a',
      '--accent': '#39ff88',
      '--accent-cool': '#c6ff2e',
      '--gold': '#ffe14d',
      '--good': '#39ff88',
      '--warn': '#ff5e8a',
      '--glow': '0 0 22px rgba(57, 255, 136, 0.42)',
    },
  },
  {
    id: 'friday',
    label: 'Friday',
    note: 'Ember orange over rust',
    vars: {
      '--bg': '#1a0a06',
      '--bg-raised': '#33130a',
      '--bg-sunken': '#0f0503',
      '--line': '#5c2411',
      '--line-strong': '#9c3d1a',
      '--ink': '#fff3e8',
      '--ink-dim': '#f0b48a',
      '--ink-faint': '#b8825f',
      '--accent': '#ff7a1a',
      '--accent-cool': '#ffd23f',
      '--gold': '#ffd23f',
      '--good': '#5fe08a',
      '--warn': '#ff3d5e',
      '--glow': '0 0 22px rgba(255, 122, 26, 0.45)',
    },
  },
  {
    id: 'wompy',
    label: 'Wompy Woods',
    note: 'Acid green and violet',
    vars: {
      '--bg': '#0b0f1c',
      '--bg-raised': '#141a30',
      '--bg-sunken': '#070a14',
      '--line': '#252f52',
      '--line-strong': '#3b4a7a',
      '--ink': '#eaf3ff',
      '--ink-dim': '#a6b6d8',
      '--ink-faint': '#6f7fa6',
      '--accent': '#a4ff3f',
      '--accent-cool': '#9d7bff',
      '--gold': '#ffe14d',
      '--good': '#7cf07c',
      '--warn': '#ff5fa2',
      '--glow': '0 0 18px rgba(164, 255, 63, 0.3)',
    },
  },
  {
    id: 'crater',
    label: 'The Crater',
    note: 'Ice blue and magenta',
    vars: {
      '--bg': '#0a0f1e',
      '--bg-raised': '#121a33',
      '--bg-sunken': '#060a15',
      '--line': '#22305a',
      '--line-strong': '#38508c',
      '--ink': '#eaf6ff',
      '--ink-dim': '#9fc0dd',
      '--ink-faint': '#6a87a8',
      '--accent': '#3fd8ff',
      '--accent-cool': '#ff5fd2',
      '--gold': '#ffd166',
      '--good': '#4fe0c0',
      '--warn': '#ff5f8a',
      '--glow': '0 0 18px rgba(63, 216, 255, 0.32)',
    },
  },
  {
    id: 'forest',
    label: 'Forest Stage',
    note: 'Laser green over deep teal',
    vars: {
      '--bg': '#071414',
      '--bg-raised': '#0e2222',
      '--bg-sunken': '#040d0d',
      '--line': '#1a3a38',
      '--line-strong': '#2b5c58',
      '--ink': '#e9fff7',
      '--ink-dim': '#9fd0c2',
      '--ink-faint': '#6c968b',
      '--accent': '#54ffb0',
      '--accent-cool': '#39c8ff',
      '--gold': '#ffe066',
      '--good': '#54ffb0',
      '--warn': '#ff7a9a',
      '--glow': '0 0 18px rgba(84, 255, 176, 0.3)',
    },
  },
  {
    id: 'subsidia',
    label: 'Subsidia',
    note: 'Hot pink on black',
    vars: {
      '--bg': '#0d0810',
      '--bg-raised': '#1a1020',
      '--bg-sunken': '#080409',
      '--line': '#33203c',
      '--line-strong': '#523061',
      '--ink': '#fbeaff',
      '--ink-dim': '#cba7d8',
      '--ink-faint': '#94749f',
      '--accent': '#ff3fd0',
      '--accent-cool': '#9d5cff',
      '--gold': '#ffcf4d',
      '--good': '#6ee7a8',
      '--warn': '#ff5f6d',
      '--glow': '0 0 18px rgba(255, 63, 208, 0.32)',
    },
  },
  {
    id: 'midnight',
    label: 'Midnight',
    note: 'Plain dark, least battery on an OLED phone',
    vars: {
      '--bg': '#0b0d12',
      '--bg-raised': '#12151d',
      '--bg-sunken': '#080a0e',
      '--line': '#232936',
      '--line-strong': '#333c4e',
      '--ink': '#e7ebf3',
      '--ink-dim': '#96a0b5',
      '--ink-faint': '#64708a',
      '--accent': '#e0653a',
      '--accent-cool': '#4bb3c4',
      '--gold': '#c8a33a',
      '--good': '#5fa860',
      '--warn': '#d1567f',
      '--glow': 'none',
    },
  },
];

// Every stage has its own color on the published graphics, and the columns are
// unreadable without it. Taken from the 2026 stage art.
export const STAGE_COLORS: Record<string, string> = {
  'stage-prehistoric': '#ff4d1f',
  'stage-wompy': '#b14dff',
  'stage-crater': '#19dcd0',
  'stage-forest': '#7bed4a',
  'stage-subsidia': '#ff2fa0',
  'stage-raptor': '#3fa9ff',
  'stage-grove': '#c8ff2e',
  'stage-camp': '#ffb340',
};

// The second hue each stage's art resolves into, so a block is a gradient
// across two colors rather than one fill at two opacities.
export const STAGE_COLORS_2: Record<string, string> = {
  'stage-prehistoric': '#ffc61a',
  'stage-wompy': '#2ff0ff',
  'stage-crater': '#3f6bff',
  'stage-forest': '#19dcd0',
  'stage-subsidia': '#b14dff',
  'stage-raptor': '#39ff88',
  'stage-grove': '#39ff88',
  'stage-camp': '#ff6bd0',
};

export const stageColor = (stageId: string): string => STAGE_COLORS[stageId] ?? '#9d7bff';
export const stageColor2 = (stageId: string): string => STAGE_COLORS_2[stageId] ?? '#2ff0ff';

// Each day's poster runs its own scheme. The day tabs carry it.
export const DAY_COLORS: Record<string, string> = {
  'day-wed': '#38bdf8',
  'day-thu': '#5eead4',
  'day-fri': '#ff7a1a',
  'day-sat': '#39ff88',
  'day-sun': '#ff2fd0',
};

export const dayColor = (dayId: string): string => DAY_COLORS[dayId] ?? '#ff2fd0';

// The foliage bank that stands along the foot of the page, one recolor per
// theme so the plants match whatever palette is running.
export const FOLIAGE: Record<string, string> = {
  poster: '/art/foliage-8.webp',
  saturday: '/art/foliage-5.webp',
  friday: '/art/foliage-7.webp',
  wompy: '/art/foliage-8.webp',
  crater: '/art/foliage-6.webp',
  forest: '/art/foliage-5.webp',
  subsidia: '/art/foliage-8.webp',
  midnight: '/art/foliage-6.webp',
};

export const foliageFor = (themeId: string): string => FOLIAGE[themeId] ?? FOLIAGE.poster;

export interface FontChoice {
  id: string;
  label: string;
  display: string;
  body: string;
  /** Google Fonts family names to load, or null for the system stack. */
  load: string | null;
}

export const FONTS: FontChoice[] = [
  {
    id: 'poster',
    label: 'Poster',
    display: "'Bebas Neue', 'Arial Narrow', system-ui, sans-serif",
    body: "'Chakra Petch', ui-sans-serif, system-ui, sans-serif",
    load: 'Bebas+Neue&family=Chakra+Petch:wght@400;500;600;700',
  },
  {
    id: 'rave',
    label: 'Rave',
    display: "'Orbitron', system-ui, sans-serif",
    body: "'Rajdhani', ui-sans-serif, system-ui, sans-serif",
    load: 'Orbitron:wght@600;700;800;900&family=Rajdhani:wght@400;500;600;700',
  },
  {
    id: 'anton',
    label: 'Anton',
    display: "'Anton', 'Arial Narrow', system-ui, sans-serif",
    body: "'Barlow', ui-sans-serif, system-ui, sans-serif",
    load: 'Anton&family=Barlow:wght@400;500;600;700',
  },
  {
    id: 'blocky',
    label: 'Blocky',
    display: "'Archivo Black', system-ui, sans-serif",
    body: "'Archivo', ui-sans-serif, system-ui, sans-serif",
    load: 'Archivo+Black&family=Archivo:wght@400;500;600;700',
  },
  {
    id: 'clean',
    label: 'Clean',
    display: "'Inter', ui-sans-serif, system-ui, sans-serif",
    body: "'Inter', ui-sans-serif, system-ui, sans-serif",
    load: 'Inter:wght@400;500;600;700;800',
  },
  {
    id: 'serif',
    label: 'Serif',
    display: "'Playfair Display', Georgia, serif",
    body: "'Source Serif 4', Georgia, serif",
    load: 'Playfair+Display:wght@600;700;800&family=Source+Serif+4:opsz,wght@8..60,400;8..60,600',
  },
  {
    id: 'system',
    label: 'System',
    display: 'ui-sans-serif, -apple-system, system-ui, sans-serif',
    body: 'ui-sans-serif, -apple-system, system-ui, sans-serif',
    load: null,
  },
];

export type Density = 'compact' | 'comfortable' | 'roomy';
export type GridLayout = 'stages' | 'list';

export type BeamPattern =
  | 'diagonal'
  | 'steep'
  | 'flat'
  | 'cross'
  | 'burst-up'
  | 'burst-down'
  | 'fan'
  | 'off';

export const BEAM_PATTERNS: { id: BeamPattern; label: string; angle: number; note: string }[] = [
  { id: 'diagonal', label: 'Diagonal', angle: 68, note: 'One bank sweeping across' },
  { id: 'steep', label: 'Steep', angle: 84, note: 'Near vertical, tight' },
  { id: 'flat', label: 'Flat', angle: 32, note: 'Low and wide' },
  { id: 'cross', label: 'Crossed', angle: 112, note: 'Two banks against each other' },
  { id: 'burst-up', label: 'Burst up', angle: 68, note: 'Rays from the foot of the stage' },
  { id: 'burst-down', label: 'Burst down', angle: 68, note: 'Rays from the rig overhead' },
  { id: 'fan', label: 'Fan', angle: 68, note: 'A slow sweep from one side' },
  { id: 'off', label: 'None', angle: 68, note: 'Nothing moving' },
];

// The hues the beams themselves run, separately from the palette the interface
// uses, because the light in the room is not the same choice as the ink.
export const BEAM_COLORS: { id: string; label: string; a: string; b: string; c: string }[] = [
  { id: 'theme', label: 'Match theme', a: 'var(--accent-cool)', b: 'var(--gold)', c: 'var(--accent)' },
  { id: 'poster', label: 'Poster', a: '#2ff0ff', b: '#c6ff2e', c: '#ff2fd0' },
  { id: 'acid', label: 'Acid', a: '#c6ff2e', b: '#39ff88', c: '#2ff0ff' },
  { id: 'ember', label: 'Ember', a: '#ff7a1a', b: '#ffd23f', c: '#ff3d5e' },
  { id: 'ice', label: 'Ice', a: '#7fd8ff', b: '#c9f2ff', c: '#9d7bff' },
  { id: 'white', label: 'White', a: '#ffffff', b: '#dfe8ff', c: '#ffffff' },
];

export interface Settings {
  theme: string;
  font: string;
  density: Density;
  layout: GridLayout;
  showArt: boolean;
  hourHeight: number;
  beamPattern: BeamPattern;
  /** Percentage of the default beam speed; 100 is the original drift. */
  beamSpeed: number;
  beamColor: string;
  /** Where the footer starts, as a share of the viewport the content holds. */
  contentHeight: number;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'poster',
  font: 'poster',
  density: 'comfortable',
  layout: 'stages',
  showArt: true,
  hourHeight: 84,
  beamPattern: 'diagonal',
  beamSpeed: 100,
  beamColor: 'theme',
  contentHeight: 100,
};

// Density moves the body size, the padding inside a panel and the gap between
// panels together. Moving only the font left every setting looking the same.
const DENSITY_SCALE: Record<Density, { font: number; rowPad: number; rowGap: number; panelPad: number; gap: number }> = {
  compact: { font: 15, rowPad: 6, rowGap: 2, panelPad: 12, gap: 12 },
  comfortable: { font: 17, rowPad: 11, rowGap: 5, panelPad: 20, gap: 18 },
  roomy: { font: 19, rowPad: 18, rowGap: 9, panelPad: 30, gap: 28 },
};

const STORAGE_KEY = 'festival-scheduler-settings';

export function readSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function writeSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // A browser refusing storage is not a reason to fail a theme change.
  }
}

let loadedFont: string | null = null;

function loadFont(choice: FontChoice): void {
  if (!choice.load || loadedFont === choice.id) return;
  const id = 'festival-font-link';
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  const href = `https://fonts.googleapis.com/css2?family=${choice.load}&display=swap`;
  if (existing) existing.href = href;
  else {
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }
  loadedFont = choice.id;
}

// One extra stylesheet per family a profile page asks for, kept apart from the
// site's own font link so choosing one there does not replace the other.
const extraFonts = new Set<string>();

export function ensureFont(id: string | null | undefined): void {
  if (!id || extraFonts.has(id)) return;
  const choice = FONTS.find((f) => f.id === id);
  if (!choice?.load) return;
  extraFonts.add(id);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${choice.load}&display=swap`;
  document.head.appendChild(link);
}

/** Writes a settings object onto the document root. */
export function applySettings(settings: Settings): void {
  const theme = THEMES.find((t) => t.id === settings.theme) ?? THEMES[0];
  const font = FONTS.find((f) => f.id === settings.font) ?? FONTS[0];
  const density = DENSITY_SCALE[settings.density] ?? DENSITY_SCALE.comfortable;
  const root = document.documentElement;

  for (const [key, value] of Object.entries(theme.vars)) root.style.setProperty(key, value);
  root.style.setProperty('--font-display', font.display);
  root.style.setProperty('--font-body', font.body);
  root.style.setProperty('--base-size', `${density.font}px`);
  root.style.setProperty('--row-pad', `${density.rowPad}px`);
  root.style.setProperty('--row-gap', `${density.rowGap}px`);
  root.style.setProperty('--panel-pad', `${density.panelPad}px`);
  root.style.setProperty('--gap', `${density.gap}px`);
  root.style.setProperty('--grid-hour', `${settings.hourHeight}px`);
  root.style.setProperty('--foliage', `url("${foliageFor(theme.id)}")`);

  const beam = BEAM_PATTERNS.find((b) => b.id === settings.beamPattern) ?? BEAM_PATTERNS[0];
  const speed = Math.min(400, Math.max(20, settings.beamSpeed || 100));
  const hues = BEAM_COLORS.find((c) => c.id === settings.beamColor) ?? BEAM_COLORS[0];
  root.style.setProperty('--beam-angle', `${beam.angle}deg`);
  root.style.setProperty('--beam-seconds', `${Math.round((46 * 100) / speed)}s`);
  root.style.setProperty('--beam-a', hues.a);
  root.style.setProperty('--beam-b', hues.b);
  root.style.setProperty('--beam-c', hues.c);
  root.dataset.beams = beam.id;

  // The share of the viewport the content holds before the footer begins, which
  // is what the handle under a page drags.
  const content = Math.min(200, Math.max(60, settings.contentHeight || 100));
  root.style.setProperty('--content-min', `${content}dvh`);

  root.dataset.theme = theme.id;
  root.dataset.layout = settings.layout;
  root.dataset.art = settings.showArt ? 'on' : 'off';
  loadFont(font);
}
